import { useMemo } from 'react';
import { PlugZapIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
  StatTile,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { clientLabel, readMcpUsage, type McpUsageUser } from '@/lib/mcp-usage';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';

/** The connector URL people add to Claude, shown in the empty state. */
const MCP_CONNECTOR_URL = 'https://mcp.bcecloud.com/mcp';

/** Sortable columns → the flattened row field each orders by. */
const MCP_USAGE_SORT_FIELDS = {
  person: 'name',
  lastUsed: 'lastUsedAt',
  calls7d: 'calls7d',
  calls30d: 'calls30d',
  errorRate: 'errorRate30d',
  clients: 'clientNames',
} as const;
const MCP_USAGE_SORT_KEYS = Object.values(MCP_USAGE_SORT_FIELDS);

/** Default ordering: most recent use first, so the live users lead. */
const DEFAULT_SORT = { key: MCP_USAGE_SORT_FIELDS.lastUsed, direction: 'desc' } as const;

/** An error rate at or above this reads as a problem worth a look. */
const HIGH_ERROR_RATE = 0.2;

/** One table row: list fields flattened into strings so search and sort can use them. */
interface McpUsageRow extends McpUsageUser {
  readonly clientNames: string;
  readonly toolNames: string;
}

/** The row fields the search box matches. */
const MCP_USAGE_SEARCH_FIELDS: ReadonlyArray<keyof McpUsageRow> = [
  'name',
  'email',
  'clientNames',
  'toolNames',
];

function toRow(user: McpUsageUser): McpUsageRow {
  return {
    ...user,
    clientNames: [...new Set(user.clients.map(clientLabel))].join(', '),
    toolNames: user.topTools.map((tool) => tool.toolName).join(', '),
  };
}

/** A relative "last used" reading, falling back to a date past a month. */
function lastUsedLabel(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** A 0–1 share as a whole percentage, or an em dash with nothing to measure. */
function percent(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}

/**
 * Analytics → MCP usage: everyone who has used the bcecloud connector from
 * Claude or ChatGPT, how much, from which client, and how often it failed.
 * Gated on view_analytics (the API enforces the same grant).
 *
 * @returns The MCP usage page.
 */
export function McpUsagePage(): React.ReactElement {
  const { sort, onSort } = useTableSort(MCP_USAGE_SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();

  const report = useQuery({
    queryKey: ['mcp-usage'],
    queryFn: ({ signal }) => readMcpUsage(signal),
    staleTime: 60_000,
  });

  const rows = useMemo<readonly McpUsageRow[]>(
    () => sortRows(filterRows((report.data?.users ?? []).map(toRow), search, MCP_USAGE_SEARCH_FIELDS), sort),
    [report.data, search, sort],
  );

  const totals = report.data?.totals;
  const windowDays = report.data?.windowDays;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ANALYTICS"
        title="MCP usage"
        subtitle="Who uses the bcecloud connector in Claude or ChatGPT, from which client, and what fails. We record the tool and the outcome, never what was asked or returned."
      />

      {totals !== undefined && windowDays !== undefined ? (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile label="People connected" value={String(totals.users)} hint="Ever made a tool call" />
          <StatTile
            label={`Active in the last ${windowDays.recent} days`}
            value={String(totals.activeUsers7d)}
            hint={`${totals.calls7d} calls`}
          />
          <StatTile
            label={`Calls in the last ${windowDays.month} days`}
            value={String(totals.calls30d)}
            hint="Across everyone"
          />
          <StatTile
            label={`Error rate, last ${windowDays.month} days`}
            value={percent(totals.errorRate30d)}
            hint="Failed calls as a share of all calls"
          />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search people"
          placeholder="Name, email, client or tool"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        {report.data === undefined ? null : (
          <span className="text-portal-micro text-portal-muted">
            {rows.length} {rows.length === 1 ? 'person' : 'people'}
          </span>
        )}
      </div>

      {report.isError ? (
        <ErrorNotice title="MCP usage unavailable" message={errorMessage(report.error)} />
      ) : report.isPending ? (
        <SkeletonTable label="Loading MCP usage…" />
      ) : rows.length === 0 ? (
        <Panel>
          {search.trim() !== '' ? (
            <EmptyState message="No people match that search." />
          ) : (
            <EmptyState
              icon={<PlugZapIcon aria-hidden className="size-6" />}
              title="Nobody has used the connector yet"
              message={`People appear here after their first tool call through ${MCP_CONNECTOR_URL}.`}
            />
          )}
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Person', sortKey: MCP_USAGE_SORT_FIELDS.person },
            { label: 'Last used', sortKey: MCP_USAGE_SORT_FIELDS.lastUsed },
            { label: 'Calls (7 days)', sortKey: MCP_USAGE_SORT_FIELDS.calls7d },
            { label: 'Calls (30 days)', sortKey: MCP_USAGE_SORT_FIELDS.calls30d },
            { label: 'Error rate', sortKey: MCP_USAGE_SORT_FIELDS.errorRate },
            { label: 'Clients', sortKey: MCP_USAGE_SORT_FIELDS.clients },
            'Top tools (30 days)',
          ]}
          rightAlignedHeaders={[2, 3, 4]}
          sort={sort}
          onSort={onSort}
          minWidth={960}
        >
          {rows.map((row) => (
            <tr key={row.userId} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <span className="block font-bold text-portal-ink">{row.name}</span>
                <span className="block text-portal-micro text-portal-muted">{row.email}</span>
              </td>
              <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">
                <time dateTime={row.lastUsedAt}>{lastUsedLabel(row.lastUsedAt)}</time>
              </td>
              <td className="p-3 text-right text-portal-small">{row.calls7d}</td>
              <td className="p-3 text-right text-portal-small">{row.calls30d}</td>
              <td className="p-3 text-right text-portal-small whitespace-nowrap">
                {row.errorRate30d !== null && row.errorRate30d >= HIGH_ERROR_RATE ? (
                  <Pill tone="danger">{percent(row.errorRate30d)}</Pill>
                ) : (
                  <span className="text-portal-muted">{percent(row.errorRate30d)}</span>
                )}
              </td>
              <td className="p-3 text-portal-small">
                {row.clients.length === 0 ? (
                  <span className="text-portal-muted">Unknown</span>
                ) : (
                  <span className="flex flex-wrap gap-1">
                    {[...new Set(row.clients.map(clientLabel))].map((label) => (
                      <Pill key={label} tone="info">
                        {label}
                      </Pill>
                    ))}
                  </span>
                )}
              </td>
              <td className="p-3 text-portal-small text-portal-muted">
                {row.topTools.length === 0
                  ? 'None in the last 30 days'
                  : row.topTools.map((tool) => `${tool.toolName} (${tool.calls})`).join(', ')}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </section>
  );
}
